// Angular imports
import { HttpClient } from '@angular/common/http';
// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';
// RxJs imports
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';
import { WorkflowNotesResultDto } from '../_models/workflow-notes-result-dto';
import { WorkflowHistoryResultDto } from '../_models/wfe-workflow-history-result-dto';
import { WFInvoiceHistoryResultDto } from '../_models/wf-invoice-history-result-dto';
import { GLSubcategorySelectorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/common/gl-account/glsubcategory-selector-result-dto';
import { APBillReclassEditorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/accounts-payable/ap-bill/ap-bill-reclass-editor-result-dto';
import { WFPaymentHistoryResultDto } from '../_models/wf-payment-history-result-dto';
import { WorkflowDetailsResultDto } from '../_models/workflow-details-result-dto';
import { BoardApprovalRulesResultDto } from '../_models/board-approval-rules-result-dto';
import { WorkflowEFTEditorResultDto } from '../_models/wf-vendor-eft-detail';
// constant imports
import { CacheDuration } from './../../_constants/cache-duration';
import { URLConstants } from '../../_constants/url-constants';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../../_services/shared-services/mp-cache-registration-service";
export class WorkflowSharedService {
    constructor(httpClient, cacheRegistrationService) {
        this.httpClient = httpClient;
        this.cacheRegistrationService = cacheRegistrationService;
        this._subjectShowDetails = new Subject();
    }
    getReclassifyEditor(baseParams) {
        const url = `${URLConstants.ccCBAPBillApiUrl}/reclassify${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(APBillReclassEditorResultDto, response)));
    }
    getSubCategories(params) {
        const url = `${URLConstants.ccCBAPBillApiUrl}/subcategories${params.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(GLSubcategorySelectorResultDto, response)));
    }
    getWorkflowReadOnlyNotes(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}wfnotes${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WorkflowNotesResultDto, response)));
    }
    saveWorkflowNewNotes(baseParams) {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowViewerBaseApiUrl}wfnotes`, classToPlain(baseParams))
            .pipe(map((responseData) => responseData.toString()));
    }
    downloadInvoice(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}downloadinvoice${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData) => new Blob([responseData])));
    }
    getBoardNotes(baseParam) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}board/notes${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WorkflowNotesResultDto, response)));
    }
    saveBoardNotes(baseParams) {
        return this.httpClient
            .post(`${URLConstants.ccWorkflowViewerBaseApiUrl}board/notes`, classToPlain(baseParams))
            .pipe(map((responseData) => responseData.toString()));
    }
    getWorkflowHistory(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}wfhistory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WorkflowHistoryResultDto, response)));
    }
    getWorkflowVendorHistory(baseParams, clearCache = true) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/history${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFInvoiceHistoryResultDto, response)));
    }
    getWorkflowInvoiceHistory(baseParams, clearCache = true) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}invoice/history${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFInvoiceHistoryResultDto, response)));
    }
    getWorkflowVendorAccountHistory(baseParams, clearCache = true) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/accounthistory${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFInvoiceHistoryResultDto, response)));
    }
    getWorkflowPaymentHistory(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}payment/history${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(WFPaymentHistoryResultDto, response)));
    }
    reclassifyBill(baseParam) {
        const url = `${URLConstants.ccCBAPBillApiUrl}/reclassify`;
        return this.httpClient.post(url, classToPlain(baseParam)).pipe(map((responseData) => responseData));
    }
    getWorkflowDetail(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}details${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response) => plainToClass(WorkflowDetailsResultDto, response)));
    }
    getBoardApprovalRules(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}board-approval-rules${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((response) => plainToClass(BoardApprovalRulesResultDto, response)));
    }
    getEFTEditor(baseParams) {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}eft/editor${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((response) => plainToClass(WorkflowEFTEditorResultDto, response)));
    }
    updateDetailsParams(value) {
        this._subjectShowDetails.next(value);
    }
    getDetailsParams() {
        return this._subjectShowDetails;
    }
}
WorkflowSharedService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function WorkflowSharedService_Factory() { return new WorkflowSharedService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.MPCacheRegistrationService)); }, token: WorkflowSharedService, providedIn: "root" });
